// React imports
import React, { memo, useMemo, useCallback } from 'react';

// Material UI imports
import { Grid } from '@mui/material';

// ERP Common imports - Form builder elements
import DynamicInput from '@/components/form-control/form-builder/form-builder-element/text';
import DynamicCheckBox from '@/components/form-control/form-builder/form-builder-element/checkbox';
import DynamicTable from '@/components/form-control/form-builder/form-builder-element/table';
import DynamicElementHOC from '@/components/form-control/form-builder/form-builder-element/dynamic-element-hoc';
import DynamicRadioButton from '@/components/form-control/form-builder/form-builder-element/radio-button';
import DynamicToggleButton from '@/components/form-control/form-builder/form-builder-element/toggle-button';
import DynamicInfo from '@/components/form-control/form-builder/form-builder-element/info';
import DynamicMedia from '@/components/form-control/form-builder/form-builder-element/media';
import DynamicTime from '@/components/form-control/form-builder/form-builder-element/time';
import DynamicDate from '@/components/form-control/form-builder/form-builder-element/date';
import DynamicSelect from '@/components/form-control/form-builder/form-builder-element/select';
import DynamicPhone from '@/components/form-control/form-builder/form-builder-element/phone';

// Types
interface IField {
  id: string;
  field_type: string;
  placeholder?: string;
  label: string;
  default_value?: any;
  is_multiline?: boolean;
  is_required?: boolean;
  min_length?: number;
  max_length?: number;
  is_fullwidth?: boolean;
  custom_width?: number;
  options?: any[];
  is_multiselect?: boolean;
  is_future_dates_allowed?: boolean;
  is_past_dates_allowed?: boolean;
  option?: any;
  max_size?: number;
  title?: string;
  display?: string;
  title_position?: string;
  table_columns?: any[];
  float_step?: number;
  type: string;
}

interface FormFieldsProps {
  sectionMembers: IField[];
  // Add other required props with proper types
  members: IField[];
  activeMember?: number;
  control: any;
  t: (key: string) => string;
  element: any;
  images: any;
  elementJSON?: any;
  remove: (index: number) => void;
  move: (from: number, to: number) => void;
  handleElementSelection: (e: any) => void;
  setElementSwap: (value: boolean) => void;
  handleDuplicateElement: (index: number) => void;
  setSelectedHoc: (value: string) => void;
  setActiveMember: (value: number | undefined) => void;
}

// Field type mapping for better maintainability
const FIELD_TYPE_MAP = {
  TEXT: 'text',
  EMAIL: 'email',
  NUMBER: 'number',
  PHONE: 'phone',
  SELECT: 'select',
  DATE: 'date',
  TIME: 'time',
  URL: 'url',
  CURRENCY: 'currency',
  FILE: 'file',
  IMAGE: 'image',
  GEO_LOCATION: 'geoLocation',
  RELATION: 'relation',
  INFO: 'info',
  TOGGLE_BUTTON: 'toggleButton',
  RADIO_BUTTON: 'radioButton',
  TAGS_INPUT: 'tagsInput',
  CHECKBOX: 'checkbox',
  TABLE: 'table'
} as const;

// Memoized individual field component
const FormField = memo<{
  field: IField;
  index: number;
  gridItemSize: number;
  activeMember?: number;
  control: any;
  t: (key: string) => string;
  element: any;
  images: any;
  elementJSON?: any;
  remove: (index: number) => void;
  move: (from: number, to: number) => void;
  handleElementSelection: (e: any) => void;
  setElementSwap: (value: boolean) => void;
  handleDuplicateElement: (index: number) => void;
  setSelectedHoc: (value: string) => void;
  setActiveMember: (value: number | undefined) => void;
}>(({
  field,
  index,
  gridItemSize,
  activeMember,
  control,
  t,
  element,
  images,
  elementJSON,
  remove,
  move,
  handleElementSelection,
  setElementSwap,
  handleDuplicateElement,
  setSelectedHoc,
  setActiveMember
}) => {
  // Common HOC props - memoized to prevent unnecessary re-renders
  const hocProps = useMemo(() => ({
    activeIndex: activeMember === index,
    removeElement: remove,
    index,
    onClick: handleElementSelection,
    swap: move,
    elementSwap: () => setElementSwap(true),
    handleDuplicate: handleDuplicateElement,
    setElementHoc: () => {
      setSelectedHoc("");
      setActiveMember(undefined);
    }
  }), [
    activeMember, 
    index, 
    remove, 
    handleElementSelection, 
    move, 
    setElementSwap, 
    handleDuplicateElement, 
    setSelectedHoc, 
    setActiveMember
  ]);

  // Common field props - memoized to prevent unnecessary re-renders
  const commonFieldProps = useMemo(() => ({
    key: field.id,
    position: index,
    placeholder: t(field.placeholder || ''),
    label: t(field.label),
    formControl: control,
    name: "message",
    fieldArrayName: "members",
    required: field.is_required,
    formType: "builder" as const,
    typeOfField: field.type,
    disabled: true
  }), [field.id, field.placeholder, field.label, field.is_required, field.type, index, t, control]);

  // Render field based on type
  const renderFieldContent = useCallback(() => {
    const { field_type } = field;

    switch (field_type) {
      case element.text.field_type:
        return (
          <DynamicInput
            {...commonFieldProps}
            type={field_type}
            defaultValue={field.default_value}
            is_multiline={field.is_multiline}
            min={field.min_length}
            max={field.max_length}
          />
        );

      case element.email.field_type:
        return (
          <DynamicInput
            {...commonFieldProps}
            type={field_type}
            defaultValue={field.default_value}
            endIcon={<img src={images.sms} alt="email" />}
          />
        );

      case element.number.field_type:
        return (
          <DynamicInput
            {...commonFieldProps}
            type={field_type}
            defaultValue={field.default_value}
            min={field.min_length}
            max={field.max_length}
            float_step={field.float_step}
          />
        );

      case element.phone.field_type:
        return (
          <DynamicPhone
            {...commonFieldProps}
            type={field_type}
            default_value={field.default_value}
          />
        );

      case element.select.field_type:
        return (
          <DynamicSelect
            {...commonFieldProps}
            options={field.options}
            defaultValue={field.default_value}
            is_multiselect={field.is_multiselect}
          />
        );

      case element.date.field_type:
        return (
          <DynamicDate
            {...commonFieldProps}
            defaultValue={field.default_value}
            is_future_dates_allowed={field.is_future_dates_allowed}
            is_past_dates_allowed={field.is_past_dates_allowed}
            disable={true}
          />
        );

      case element.time.field_type:
        return (
          <DynamicTime
            {...commonFieldProps}
            option={field.option}
          />
        );

      case element.url.field_type:
        return (
          <DynamicInput
            {...commonFieldProps}
            type={field_type}
            defaultValue={field.default_value}
            endIcon={<img src={images.url} alt="url" />}
          />
        );

      case element.currency.field_type:
        return (
          <DynamicInput
            {...commonFieldProps}
            type={field_type}
            defaultValue={field.default_value}
            endIcon={<img src={images.dollar} alt="currency" />}
          />
        );

      case element.file.field_type:
      case element.image.field_type:
        return (
          <DynamicMedia
            {...commonFieldProps}
            type={field_type}
            max_size={field.max_size}
          />
        );

      case element.geoLocation.field_type:
        return (
          <DynamicInput
            {...commonFieldProps}
            type={field_type}
            defaultValue={field.default_value}
            endIcon={<img src={images.location} alt="location" />}
          />
        );

      case element.relation.field_type:
        return (
          <DynamicInput
            {...commonFieldProps}
            type={field_type}
            defaultValue={field.default_value}
            is_multiline={field.is_multiline}
            min={field.min_length}
            max={field.max_length}
            label={
              <span
                className="formSection--Relation"
                data-position={index}
                data-name={`members.${index}.message`}
              >
                {t(field.label)}
                <img src={images.relation} alt="relation" />
              </span>
            }
          />
        );

      case element.info.field_type:
        return (
          <DynamicInfo
            {...commonFieldProps}
            defaultValue={field.default_value}
          />
        );

      case element.toggleButton.field_type:
        return (
          <DynamicToggleButton
            {...commonFieldProps}
            title={t(field.title || '')}
            titlePosition={elementJSON?.title_position}
          />
        );

      case element.radioButton.field_type:
        return (
          <DynamicRadioButton
            {...commonFieldProps}
            options={field.options}
            display={field.display}
            titlePosition={elementJSON?.title_position}
          />
        );

      case element.tagsInput.field_type:
        return (
          <DynamicInput
            {...commonFieldProps}
            type={field_type}
            defaultValue={field.default_value}
            is_multiline={field.is_multiline}
            min={field.min_length}
            max={field.max_length}
          />
        );

      case element.checkbox.field_type:
        return (
          <DynamicCheckBox
            {...commonFieldProps}
            title={t(field.title || '')}
            titlePosition={field.title_position}
          />
        );

      case element.table.field_type:
        return (
          <DynamicTable
            {...commonFieldProps}
            columns={field.table_columns || elementJSON?.table_columns || []}
          />
        );

      default:
        console.warn(`Unknown field type: ${field_type}`);
        return null;
    }
  }, [field, commonFieldProps, element, images, elementJSON, t, index]);

  return (
    <Grid item xs={gridItemSize}>
      <DynamicElementHOC
        {...hocProps}
        element={field.field_type}
      >
        {renderFieldContent()}
      </DynamicElementHOC>
    </Grid>
  );
});

FormField.displayName = 'FormField';

// Main component
const FormFields: React.FC<FormFieldsProps> = memo(({
  sectionMembers,
  members,
  activeMember,
  control,
  t,
  element,
  images,
  elementJSON,
  remove,
  move,
  handleElementSelection,
  setElementSwap,
  handleDuplicateElement,
  setSelectedHoc,
  setActiveMember
}) => {
  // Memoize grid item size calculation
  const calculateGridSize = useCallback((field: IField): number => {
    if (field.is_fullwidth || field.field_type === "table") return 12;
    if (field.custom_width) return field.custom_width;
    return 6;
  }, []);

  // Memoize the rendered fields
  const renderedFields = useMemo(() => {
    return sectionMembers.map((field: IField) => {
      const index = members.findIndex(member => member === field);
      const gridItemSize = calculateGridSize(field);

      return (
        <FormField
          key={`${field.id}-${index}`} // Better key for React reconciliation
          field={field}
          index={index}
          gridItemSize={gridItemSize}
          activeMember={activeMember}
          control={control}
          t={t}
          element={element}
          images={images}
          elementJSON={elementJSON}
          remove={remove}
          move={move}
          handleElementSelection={handleElementSelection}
          setElementSwap={setElementSwap}
          handleDuplicateElement={handleDuplicateElement}
          setSelectedHoc={setSelectedHoc}
          setActiveMember={setActiveMember}
        />
      );
    });
  }, [
    sectionMembers,
    members,
    activeMember,
    control,
    t,
    element,
    images,
    elementJSON,
    remove,
    move,
    handleElementSelection,
    setElementSwap,
    handleDuplicateElement,
    setSelectedHoc,
    setActiveMember,
    calculateGridSize,
    elementJSON?.field_type
  ]);

  return <>{renderedFields}</>;
});

FormFields.displayName = 'FormFields';

export { FormField };
export default FormFields;